import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { config } from '../config'

interface User {
  id: string
  email: string
  first_name?: string
  last_name?: string
  company_name?: string
  onboarding_completed?: boolean
}

export interface LoginResponse {
  success: boolean
  user_id?: string
  user?: User
  must_change_password?: boolean
  message?: string
}

interface AuthContextType {
  user: User | null
  isReviewer: boolean
  login: (email: string, password: string) => Promise<LoginResponse>
  register: (payload: { email: string; password: string; first_name?: string; last_name?: string; company_name?: string }) => Promise<void>
  logout: () => void
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isReviewer, setIsReviewer] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check for existing session
    const checkAuth = async () => {
      try {
        const response = await fetch(`${config.apiBaseUrl}/api/auth/me`, { credentials: 'include' })
        if (response.ok) {
          const userData = await response.json()
          // Support both {user: {...}} and bare user shapes
          const userObj = (userData as any).user ?? userData
          setUser(userObj)
          
          // Check if user is a reviewer using dedicated endpoint
          try {
            const reviewerCheck = await fetch(`${config.apiBaseUrl}/api/auth/reviewer-status`, { 
              credentials: 'include' 
            })
            if (reviewerCheck.ok) {
              const data = await reviewerCheck.json()
              setIsReviewer(data.is_reviewer || false)
            } else {
              setIsReviewer(false)
            }
          } catch {
            setIsReviewer(false)
          }
        }
        // Silently handle not authenticated - this is expected behavior
      } catch (error) {
        // Gracefully handle backend connection errors (backend may not be running during UI development)
        // Only log in development mode to reduce console noise
        if (import.meta.env.DEV) {
          console.debug('[useAuth] Backend not available (expected in development):', error)
        }
        // Don't set user, but also don't block UI - allow development to continue
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  const login = async (email: string, password: string): Promise<LoginResponse> => {
    // Add timeout to prevent infinite loading
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || errorData.message || 'Invalid email or password')
      }

      const loginData: LoginResponse = await response.json()
      
      // After login, verify the session is working by calling /api/auth/me
      // This ensures the session cookie was set correctly
      try {
        const meResponse = await fetch(`${config.apiBaseUrl}/api/auth/me`, { 
          credentials: 'include' 
        })
        if (meResponse.ok) {
          const verifiedUser = await meResponse.json()
          // Use the verified user data (supports both {user: {...}} and bare user shapes)
          const verifiedUserData = (verifiedUser as any).user ?? verifiedUser
          setUser(verifiedUserData)
          
          // Check if user is a reviewer after login using dedicated endpoint
          try {
            const reviewerCheck = await fetch(`${config.apiBaseUrl}/api/auth/reviewer-status`, { 
              credentials: 'include' 
            })
            if (reviewerCheck.ok) {
              const data = await reviewerCheck.json()
              setIsReviewer(data.is_reviewer || false)
            } else {
              setIsReviewer(false)
            }
          } catch {
            setIsReviewer(false)
          }
          
          // Return login response with verified user data
          return { ...loginData, user: verifiedUserData }
        } else {
          // Session not working, but login succeeded - set user from response anyway
          // The session cookie fix should resolve this, but this is a fallback
          console.warn('[useAuth] Session verification failed after login, but user data received')
          const userData = loginData.user || { id: loginData.user_id || '', email }
          setUser(userData)
          setIsReviewer(false) // Can't check reviewer status without session
          return loginData
        }
      } catch (error) {
        // If session check fails, still set user from login response
        // The session cookie fix should resolve this
        console.warn('[useAuth] Could not verify session after login:', error)
      const userData = loginData.user || { id: loginData.user_id || '', email }
      setUser(userData)
      setIsReviewer(false) // Can't check reviewer status without session
      return loginData
      }
    } catch (error: any) {
      clearTimeout(timeoutId)
      if (error.name === 'AbortError') {
        throw new Error('Connection timeout. Please check if the server is running.')
      }
      throw error
    }
  }

  const register = async (payload: { email: string; password: string; first_name?: string; last_name?: string; company_name?: string }) => {
    const response = await fetch(`${config.apiBaseUrl}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      throw new Error('Register failed')
    }

    const userData = await response.json()
    
    // After registration, verify the session is working by calling /api/auth/me
    // This ensures the session cookie was set correctly
    try {
      const meResponse = await fetch(`${config.apiBaseUrl}/api/auth/me`, { 
        credentials: 'include' 
      })
      if (meResponse.ok) {
        const verifiedUser = await meResponse.json()
        // Use the verified user data (supports both {user: {...}} and bare user shapes)
        setUser((verifiedUser as any).user ?? verifiedUser)
      } else {
        // Session not working, but registration succeeded - set user from response anyway
        // The session cookie fix should resolve this, but this is a fallback
        console.warn('[useAuth] Session verification failed after registration, but user data received')
        setUser(userData)
      }
    } catch (error) {
      // If session check fails, still set user from registration response
      // The session cookie fix should resolve this
      console.warn('[useAuth] Could not verify session after registration:', error)
    setUser(userData)
    }
  }

  const logout = async () => {
    // Clear user state immediately for UI responsiveness
    setUser(null)
    
    // Call logout API to clear server-side session and cookies
    try {
      await fetch(`${config.apiBaseUrl}/api/auth/logout`, { 
        method: 'POST', 
        credentials: 'include' 
      })
    } catch (error) {
      // Even if logout API fails, we've cleared local state
      console.error('Logout API call failed:', error)
    }
  }

  return (
    <AuthContext.Provider value={{ user, isReviewer, login, register, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}